import { styled } from '@mui/material';

import { useEffect, useState, useTransition } from 'react';

import { useNavigate } from 'react-router';

import { useForm } from 'react-hook-form';

import posthog from 'posthog-js';

import { useI18n } from '@app-velocity/i18n';

import { ErrorMessage, Form, Radio, Select, Button } from '@app-velocity/ui';

import { ClassValidatorResolver } from '@app-velocity/validators';

import { ConvertedImageRaw } from '../../../../../../core/types';

import { useSelectedNode } from '../../hooks/useSelectedNode';
import { PagePreview } from '../PagePreview';
import { SelectedElement } from '../SelectedElement';

import { bufferToBase64 } from '../../../../../utils/image';
import { ChallengeSaveError } from '../../../../../../types';
import { NewReviewDto } from './dto/NewReviewDto';

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const Methods = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const isCorrectImageResponse = (
  imageResponse: unknown
): imageResponse is ConvertedImageRaw => {
  if (typeof imageResponse === 'string') return false;
  const correctResponse = imageResponse as ConvertedImageRaw;
  if (imageResponse && 'converted' in correctResponse) return true;

  return false;
};

export const NewReviewForm = () => {
  const { t } = useI18n();
  const { selectedNode, selectedNodeImage } = useSelectedNode();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<ChallengeSaveError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [base64, setBase64] = useState<string | null>(null);
  const { watch, setValue, register } = useForm<NewReviewDto>({
    resolver: ClassValidatorResolver(NewReviewDto),
    defaultValues: {
      method: 'accessibility',
      level: 'A',
    },
  });

  const [method, level] = watch(['method', 'level']);

  const getNodeImage = async () => {
    if (!selectedNode || !selectedNodeImage) return;
    setIsLoading(true);
    setError(null);

    if (isCorrectImageResponse(selectedNodeImage)) {
      const base64Image = selectedNodeImage.converted
        ? await bufferToBase64(selectedNodeImage.converted)
        : '';
      selectedNodeImage.converted = null;
      setBase64(base64Image);
    } else {
      setBase64(null);
      setError(selectedNodeImage);
    }

    setIsLoading(false);
  };

  const getErrorMessage = () => {
    if (!error) return null;

    switch (error.type) {
      case 'IMAGE_TOO_BIG':
        return t('appraisals:image_error.image_too_big');
      case 'NODE_NOT_FOUND':
        return t('appraisals:image_error.unable_to_find_node');
    }

    return t('appraisals:image_error.unable_to_generate_image');
  };

  useEffect(() => {
    startTransition(() => {
      getNodeImage();
    });

    if (selectedNode) {
      posthog.capture('selected_frame_to_review', {
        name: selectedNode.name,
      });
    }
  }, [selectedNode]);

  const goToNewReview = () => {
    if (!selectedNode) return;

    posthog.capture('started_design_review', {
      name: selectedNode.name,
      method,
      level: method === 'accessibility' ? method : undefined,
    });

    switch (method) {
      case 'accessibility': {
        navigate(`./${selectedNode.id}/${method}/${level}`);
        break;
      }

      case 'refactoringui': {
        navigate(`./${selectedNode.id}/${method}`);
        break;
      }
    }
  };

  const handleMethodSelect = (val: string) => {
    setValue('method', val);
    posthog.capture('selected_review_method', {
      method: val,
    });
  };

  return (
    <StyledContainer>
      <PagePreview base64Image={base64} isLoading={isPending || isLoading} />
      <Form.FieldContainer>
        <Form.Label>{t('appraisals:selected_page')}:</Form.Label>
        <SelectedElement element={selectedNode} />
      </Form.FieldContainer>
      <Form.FieldContainer>
        <Form.Label>{t('appraisals:review_method.title')}</Form.Label>
        <Methods>
          <Radio
            name="method"
            value="accessibility"
            label={t('appraisals:review_method.accessibility.title')}
            description={t(
              'appraisals:review_method.accessibility.description'
            )}
            checked={method === 'accessibility'}
            onChange={handleMethodSelect}
            right={
              <Select
                {...register('level')}
                variant="filled"
                size="small"
                defaultValue="A"
                renderValue={(selected) => `WCAG: ${selected}`}
              >
                <Select.Item value="A">A</Select.Item>
                <Select.Item value="AA">AA</Select.Item>
              </Select>
            }
          />
          <Radio
            name="method"
            value="refactoringui"
            label={t('appraisals:review_method.refactoring_ui.title')}
            description={t(
              'appraisals:review_method.refactoring_ui.description'
            )}
            checked={method === 'refactoringui'}
            onChange={handleMethodSelect}
          />
        </Methods>
      </Form.FieldContainer>
      {error ? <ErrorMessage error={getErrorMessage()} /> : null}

      <Button
        onClick={goToNewReview}
        variant="contained"
        color="primary"
        disabled={!method || !selectedNode || isPending || isLoading || !!error}
        data-ph-capture-attribute-id="start_review_button"
      >
        {t('appraisals:review')}
      </Button>
    </StyledContainer>
  );
};
